import { Button, Modal, Stack, Text, Textarea } from "@mantine/core";
import { useForm } from "@mantine/form";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ReturnsApi } from "../../apis/returns";
import { message } from "../../utils/Message";
import { handleError } from "../../utils/error-handler";
import { ModalFooter } from "../ui/app-modal";
import modalChrome from "../ui/app-modal.module.css";

interface RejectModalProps {
  opened: boolean;
  onClose: () => void;
  returnRequestId: string;
  onSuccess: () => void;
}

type FormValues = {
  rejectionReason: string;
};

export function RejectModal({ opened, onClose, returnRequestId, onSuccess }: RejectModalProps) {
  const { t } = useTranslation("returns");
  const [loading, setLoading] = useState<boolean>(false);

  const form = useForm<FormValues>({
    mode: "controlled",
    initialValues: {
      rejectionReason: "",
    },
  });

  const handleSubmit = async () => {
    if (!form.values.rejectionReason.trim()) {
      message.error(t("reject.errReason"));
      return;
    }

    try {
      setLoading(true);
      await ReturnsApi.reject(returnRequestId, {
        rejectionReason: form.values.rejectionReason.trim(),
      });
      message.success(t("reject.success"));
      onSuccess();
      handleClose();
    } catch (error) {
      handleError(error, t("reject.error"));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    form.reset();
    onClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={t("reject.modalTitle")}
      size="md"
      classNames={{ body: modalChrome.modalBodyFlush }}
    >
      <Stack gap="md" px={24} pb={20}>
        <Text size="sm" c="dimmed">
          {t("reject.description")}
        </Text>

        <Textarea
          label={t("reject.reason")}
          placeholder={t("reject.reasonPlaceholder")}
          {...form.getInputProps("rejectionReason")}
          minRows={3}
          required
        />
      </Stack>
      <ModalFooter>
        <Button variant="default" onClick={handleClose}>
          {t("action.cancel")}
        </Button>
        <Button color="red" loading={loading} onClick={handleSubmit}>
          {t("reject.submit")}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
